<template>
  <div
    v-if="hasFulfillmentOptions"
    class="mx-0 mb-2 p-2 fulfillment-options-container"
    qaid="fulfillment-option-container"
  >
    <postal-code-drawer :is-drawer-open="isPostalCodeDrawerOpen" @close="setIsPostalCodeDrawerOpen(false)" />
    <retailer-drawer :is-drawer-open="isRetailerDrawerOpen" @close="setIsRetailerDrawerOpen(false)" />
    <pricing-drawer :is-drawer-open="isPricingDrawerOpen" @close="setPricingDrawer(false)" />

    <div class="space-y-6">
      <component
        :is="option.component"
        v-for="(option, i) of fulfillmentOptions.filter((fulfillmentOption) => fulfillmentOption.component)"
        :key="`fulfillment-block-${i}`"
        :variant-code="variantCode"
        :option="option"
        :set-is-drawer-open="getDrawerSetter(option.code)"
        :set-is-pricing-drawer-open="setPricingDrawer"
      />
    </div>
  </div>
  <div v-else-if="isLoadingOptions">
    <pdl-skeleton height="4rem" />
    <pdl-skeleton height="4rem" />
    <pdl-skeleton height="4rem" />
  </div>
</template>

<script>
import ShipToHome from '@/components/containers/buy-zone/b2c/fulfillment-options/home-shipping/ShipToHome.vue';
import PickUpInStore from '@/components/containers/buy-zone/b2c/fulfillment-options/store-pick-up/PickUpInStore.vue';
import PremiumHomeDelivery from '@/components/containers/buy-zone/b2c/fulfillment-options/premium-home-delivery/PremiumHomeDelivery.vue';
import {PdlSkeleton} from '@pedal/pdl-skeleton';
import {FulfillmentOptionTypes} from '@/constants/fulfillment-options.js';
import RetailerDrawer from '@/components/drawer/retailer/RetailerDrawer.vue';
import PostalCodeDrawer from '@/components/drawer/address/PostalCodeDrawer.vue';
import {mapState, mapMutations} from 'vuex';
import PricingDrawer from '@/components/drawer/pricing/PricingDrawer.vue';
import {ProductFulfillmentConfig} from '@/utils/fulfillment-options-config.js';
import {loadVariantPricing} from '@/components/containers/pdp/utils/product-pricing';

export default {
  components: {
    ShipToHome,
    PickUpInStore,
    PremiumHomeDelivery,
    PdlSkeleton,
    RetailerDrawer,
    PostalCodeDrawer,
    PricingDrawer,
  },
  props: {
    variantCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingOptions: false,
      isRetailerDrawerOpen: false,
      isPostalCodeDrawerOpen: false,
      isPricingDrawerOpen: false,
    };
  },
  computed: {
    ...mapState('backend', ['geolocation', 'language', 'currencyIso']),
    ...mapState('user', ['selectedLocation', 'selectedRetailer']),
    ...mapState('productDetails', ['isECommAvailable', 'isLocallyActive', 'fulfillmentOptions']),

    hasFulfillmentOptions() {
      return !!this.fulfillmentOptions?.length && !!this.fulfillmentOptions?.[0] && !this.isLoadingOptions;
    },
    fulfillmentConfigPayload() {
      return {
        productCode: this.variantCode,
        isLocallyActive: this.isLocallyActive,
        isECommAvailable: this.isECommAvailable,
        storeId: this.selectedRetailer?.name,
        postalCode: this.selectedLocation?.postcode,
        regionCode: this.selectedLocation?.regionCode,
        latitude: this.selectedLocation?.latitude,
        longitude: this.selectedLocation?.longitude,
      };
    },
  },
  watch: {
    fulfillmentConfigPayload: {
      async handler(updatedPayload) {
        if (!updatedPayload) return;
        const fulfillmentOptions = await this.fetchFulfillmentOptions(updatedPayload);
        this.setFulfillmentOptions(fulfillmentOptions);
      },
      immediate: true,
    },
  },
  created() {
    loadVariantPricing(this.variantCode);
  },
  methods: {
    ...mapMutations('productDetails', ['setFulfillmentOptions']),
    getDrawerSetter(name) {
      const config = {
        [FulfillmentOptionTypes.PICK_UP_IN_STORE]: this.setIsRetailerDrawerOpen,
        [FulfillmentOptionTypes.SHIP_TO_HOME]: this.setIsPostalCodeDrawerOpen,
        [FulfillmentOptionTypes.DEALER_DELIVERY]: this.setIsPostalCodeDrawerOpen,
      };
      return config[name];
    },
    setIsRetailerDrawerOpen(value = false) {
      this.isRetailerDrawerOpen = value;
    },
    setIsPostalCodeDrawerOpen(value = false) {
      this.isPostalCodeDrawerOpen = value;
    },
    setPricingDrawer(value = false) {
      this.isPricingDrawerOpen = value;
    },
    async fetchFulfillmentOptions(options) {
      this.isLoadingOptions = true;
      try {
        const fulfillmentConfig = await ProductFulfillmentConfig.getFulfillmentConfig(options);
        return fulfillmentConfig;
      } finally {
        this.isLoadingOptions = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fulfillment-options-container {
  box-shadow: 0 15px 15px 5px var(--gray-10);
}
</style>
